/**
 * Bit-level helpers shared by the `cs-tools` family (binary calculator, two's complement,
 * IEEE 754, Hamming codes).
 *
 * Everything is BigInt, so a 64-bit pattern is exact where a JavaScript number would lose
 * its low bits above 2^53. Constants are built with `BigInt(…)` because the project
 * compiles against ES2017, where TypeScript rejects `0n` literals.
 */

import { BIG_ONE, BIG_ZERO } from "@/lib/tool-kit/math/bigint";

// biome-ignore-start lint/suspicious/noBitwiseOperators: bit masks and shifts are this module's whole job

const SEPARATORS = /[\s_]/g;
const BINARY_TOKEN = /^([+\-−]?)(?:0[bB])?([01]+)$/;
const NIBBLE_FROM_RIGHT = /(?=(?:[01]{4})+$)/g;

export type ParseFailure = "empty" | "invalid";

export type ParsedBinary =
  | { ok: true; value: bigint; negative: boolean }
  | { ok: false; error: ParseFailure };

/**
 * Read a binary integer: optional sign, optional `0b`, then 0s and 1s. Spaces and
 * underscores group digits and are ignored, so `1011 0110` and `1011_0110` both work.
 */
export function parseBinary(text: string): ParsedBinary {
  const cleaned = text.replace(SEPARATORS, "");
  if (cleaned.length === 0) {
    return { ok: false, error: "empty" };
  }
  const match = BINARY_TOKEN.exec(cleaned);
  if (!match) {
    return { ok: false, error: "invalid" };
  }
  const magnitude = BigInt(`0b${match[2]}`);
  const negative = match[1] === "-" || match[1] === "−";
  return {
    ok: true,
    value: negative ? -magnitude : magnitude,
    negative: negative && magnitude !== BIG_ZERO,
  };
}

/** Number of bits needed for a non-negative value; 0 needs none. */
export function bitLength(value: bigint): number {
  return value === BIG_ZERO ? 0 : value.toString(2).length;
}

/** `width` one-bits: 2^width − 1. */
export function maskOf(width: number): bigint {
  return (BIG_ONE << BigInt(width)) - BIG_ONE;
}

/** The unsigned bit pattern a value occupies in `width` bits (wraps like hardware). */
export function toPattern(value: bigint, width: number): bigint {
  return BigInt.asUintN(width, value);
}

/** Read a `width`-bit pattern as a two's-complement signed integer. */
export function toSigned(pattern: bigint, width: number): bigint {
  return BigInt.asIntN(width, pattern);
}

/** A pattern as exactly `width` binary digits. */
export function patternBits(pattern: bigint, width: number): string {
  return pattern.toString(2).padStart(width, "0");
}

/** Insert a space every four bits, counting from the right: `10110110` → `1011 0110`. */
export function groupNibbles(bits: string): string {
  return bits.replace(NIBBLE_FROM_RIGHT, " ").trim();
}

/** Upper-case hexadecimal, zero-padded to cover `width` bits. */
export function patternHex(pattern: bigint, width: number): string {
  return pattern
    .toString(16)
    .toUpperCase()
    .padStart(Math.ceil(width / 4), "0");
}

/** The sign-prefixed text of a signed integer in base 2 (`-101`). */
export function signedBinary(value: bigint): string {
  return value < BIG_ZERO ? `-${(-value).toString(2)}` : value.toString(2);
}

/** Hexadecimal text with a sign (`-1F`). */
export function signedHex(value: bigint): string {
  return value < BIG_ZERO
    ? `-${(-value).toString(16).toUpperCase()}`
    : value.toString(16).toUpperCase();
}

/** Octal text with a sign. */
export function signedOctal(value: bigint): string {
  return value < BIG_ZERO ? `-${(-value).toString(8)}` : value.toString(8);
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the bit helpers

const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

/** A whole number in Unicode superscript digits (`8` → `⁸`). */
export function superscript(n: number): string {
  return String(n)
    .split("")
    .map((digit) => SUPERSCRIPTS[Number(digit)] ?? digit)
    .join("");
}

/** A decimal integer with a real minus sign (`−43`). */
export function signedDecimal(value: bigint): string {
  return value < BIG_ZERO ? `−${(-value).toString()}` : value.toString();
}
